import {
    v1Exercise,
    v1Forms,
    v1Navigation,
    v1Options,
    v1i18n
} from '@/lib/api/pocket'
import { localStorageClient } from '@/lib/db/local/client'
import appState from '@/lib/domain/appstate'
import i18n from '@/lib/domain/i18n'
import fetchJson from '@/lib/fetchJson'

interface PBRecordList {
    items: v1i18n[]
    page: number
    perPage: number
    totalItems: number
    totalPages: number
}

interface PBExerciseList {
    items: v1Exercise[]
    page: number
    perPage: number
    totalItems: number
    totalPages: number
}

export interface exercisesMap {
    [key: string]: v1Exercise
}

interface PBAppContext {
    expand: {
        navigation: v1Navigation[]
        forms: v1Forms[]
        options: v1Options[]
    }
}

export async function fetchI18n(): Promise<i18n> {
    try {
        const rawData = (await fetchJson(
            `${import.meta.env.VITE_STATIC_HOST}/static/i18n.json?ref=100`,
            {
                method: 'GET'
            }
        )) as PBRecordList

        return rawData.items.reduce<i18n>((map, obj) => {
            if (obj.language === 'en' || map[obj.slug] === undefined) {
                map[obj.slug] = obj.text
            }
            map[obj.language + ':' + obj.slug] = obj.text
            return map
        }, {})
    } catch (error) {
        console.error('i18n error', error)
        return {}
    }
}

export async function fetchAppContext(): Promise<appState | undefined> {
    try {
        const rawData = (await fetchJson(
            `${import.meta.env.VITE_STATIC_HOST}/static/appcontext.json`,
            {
                method: 'GET'
            }
        )) as PBAppContext

        const data = {
            navigation: rawData.expand.navigation,
            forms: {},
            options: {},
            exercises: {}
        } as appState

        data.forms = rawData.expand.forms?.reduce((map, obj) => {
            map[obj.slug] = obj as v1Forms
            return map
        }, data.forms)

        data.options = rawData.expand.options?.reduce((map, obj) => {
            const o = obj as v1Options
            o.valueToKey = {}

            const keys = Object.keys(o.options ?? {})
            keys.forEach((k) => (o.valueToKey[o.options[k]] = k))

            map[obj.slug] = o
            return map
        }, data.options)

        return data
    } catch (error) {
        console.error('appstate fetch error', error)
        return undefined
    }
}

export async function fetchExercise(): Promise<v1Exercise[]> {
    try {
        const rawData = (await fetchJson(
            `${import.meta.env.VITE_STATIC_HOST}/static/exercises.json`,
            {
                method: 'GET'
            }
        )) as PBExerciseList

        // The cache is an optimisation; a quota or private-mode failure must
        // not throw away the exercises we just fetched.
        try {
            await localStorageClient.saveExercises(rawData.items)
        } catch (cacheError) {
            console.error('v1Exercise cache write failed', cacheError)
        }
        return rawData.items
    } catch (error) {
        console.error('v1Exercise error', error)
        return []
    }
}
